import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import Modal from '../modal/Modal';
import { SubscriptionTransferHistoryList } from './SubscriptionTransferHistory';
import { Col, Row } from '../../lib/utils';
import { usePublicMemberList } from '@/features/publicMember/hooks';
import { getMemberAppAccountStatus, ICenterMember } from '@/features/publicMember/types';
import AppAccountStatusBadge from '@/components/MemberList/AppAccountStatusBadge';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';
import { createClientOperationId } from '@/features/ticketTransfer/operationKey';
import {
    useFacilityTransferPreview,
    useFacilityTransfers,
    useTransferFacilitySubscription,
} from '@/features/facilitySubscription/hooks';
import {
    FacilityTransferBody,
    FacilityTransferPreview,
    FacilityTransferPreviewParams,
    FacilityTransferResult,
    IFacilitySubscription,
} from '@/features/facilitySubscription/types';
import {
    classifyFacilityTransferFailure,
    describeFieldBlocker,
    describeResendHeld,
    isKeyTerminalResponse,
    describeLockerBlocked,
    describeSourceBlocker,
    FacilityTransferSubmitFailure,
    isFacilityTransferUnsupported,
    isPreviewForInputs,
    isSourceBlocker,
} from '@/features/facilitySubscription/transfer';
import {
    clearPendingFacilityTransfer,
    FacilityTransferPendingRecord,
    readPendingFacilityTransfer,
    savePendingFacilityTransfer,
} from '@/features/facilitySubscription/transferPending';

/*
 * 시설 이용권 양도 창 (rappo docs/FACILITY_SUBSCRIPTION_TRANSFER_PLAN.md).
 *
 * 숫자(남은 일수·바뀐 종료일·받는 회원 기간)는 전부 서버 미리보기 값을 그대로 보여 준다. 입력이 바뀌면
 * 미리보기를 다시 읽고, 「지금 입력에 대한 미리보기」일 때만 다음 단계로 넘어간다.
 *
 * 요청 키는 창을 열 때 한 번 만들고 창이 닫힐 때까지 바꾸지 않는다.
 *  - 결과를 모르는 실패(망 오류·5xx)는 같은 키·같은 본문으로만 다시 보낸다 → 서버가 첫 결과를 돌려준다.
 *  - 그 뒤 입력을 바꿔 같은 키로 보내도, 첫 요청이 반영됐으면 서버가 409(다른 내용)로 막는다.
 *  - 창을 닫았다 다시 열면 키가 바뀐다. 그래서 미리보기의 「최근 10분 양도」를 보여 주고 한 번 더 확인받는다.
 */

const CATEGORY_LABEL: Record<string, string> = { gym: '헬스장', locker: '락커', clothing: '운동복', etc: '기타' };
const CATEGORY_ICON: Record<string, string> = { gym: '🏋️', locker: '🔐', clothing: '👕', etc: '📦' };

const fmt = (value?: string | null) => (value ? dayjs(value).format('YY.MM.DD') : '-');
const fmtShort = (value?: string | null) => (value ? dayjs(value).format('M월 D일') : '-');
const todayKey = () => dayjs().format('YYYY-MM-DD');
const resolveMemberId = (member: any) => member?.memberId || member?._id || '';

const DAYS_DEBOUNCE_MS = 300;

type Recipient = {
    id: string;
    name: string;
    phoneNumber?: string;
    member: ICenterMember;
};

type Props = {
    centerId: string;
    subscription: IFacilitySubscription;
    /** 넘겨 주는 회원 이름(지금 이용권 주인) */
    sourceMemberName: string;
    onClose: () => void;
    onDone: (result: FacilityTransferResult, recipientName: string) => void;
};

const SectionTitle = ({ step, title, hint }: { step: number; title: string; hint?: string }) => (
    <Row className="items-baseline gap-[8px] mb-[10px]">
        <span className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-[#EFF6FF] text-[#2563EB] text-[12px] font-bold shrink-0">
            {step}
        </span>
        <span className="text-[15px] font-bold text-[#111827]">{title}</span>
        {hint && <span className="text-[13px] text-[#9CA3AF]">{hint}</span>}
    </Row>
);

const FieldError = ({ message }: { message?: string | null }) =>
    message ? <span className="text-[13px] text-[#DC2626] mt-[6px]">{message}</span> : null;

const Note = ({ tone = 'info', children }: { tone?: 'info' | 'warn' | 'danger' | 'muted'; children: React.ReactNode }) => {
    const cfg = {
        info: 'bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]',
        warn: 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]',
        danger: 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]',
        muted: 'bg-[#F8FAFC] text-[#475569] border-[#E2E8F0]',
    }[tone];
    return <div className={`rounded-[10px] border border-solid px-[14px] py-[10px] text-[13px] leading-[1.6] ${cfg}`}>{children}</div>;
};

const SubscriptionTransferModal = ({ centerId, subscription, sourceMemberName, onClose, onDone }: Props) => {
    /*
     * 결과를 모르는 채 창을 닫았던 요청이 있으면 그 키·본문을 이어 쓴다(새 입력은 받지 않는다).
     * 첫 렌더에서 한 번만 읽는다. 저장소를 읽지 못하면 「없음」으로 보지 않고 새 양도를 막는다
     * (없는 줄 알고 새 키로 보내면, 남아 있던 요청이 이미 처리됐을 때 이중 양도가 된다).
     */
    const [initialRead] = useState(() => readPendingFacilityTransfer(subscription._id));
    const [pendingReadFailed, setPendingReadFailed] = useState(!initialRead.ok);
    const [restored, setRestored] = useState<FacilityTransferPendingRecord | null>(initialRead.ok ? initialRead.record : null);
    // 창 하나 = 요청 키 하나(이어 쓰는 요청이 있으면 그 키)
    const operationIdRef = useRef<string>(restored?.body.clientOperationId || createClientOperationId());
    const lastBodyRef = useRef<FacilityTransferBody | null>(restored?.body || null);
    // 결과를 모르는 요청이 남아 있는가(다시 열었거나, 이 창에서 결과 모름으로 끝났거나).
    // 이때 같은 키로 다시 보내는 응답은 종결 코드가 아니면 보관을 지우지 않는다.
    const unresolvedRef = useRef<boolean>(!!restored);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

    const [step, setStep] = useState<'input' | 'confirm'>('input');
    const [recipient, setRecipient] = useState<Recipient | null>(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [mode, setMode] = useState<'full' | 'partial'>('full');
    const [partialDaysText, setPartialDaysText] = useState('');
    const [debouncedDaysText, setDebouncedDaysText] = useState('');
    // 받는 회원 시작일. 기본값은 첫 미리보기의 기준일(원본 시작일과 오늘 중 늦은 날) — 그 전엔 비워 둔다.
    const [startDate, setStartDate] = useState('');
    const [defaultStartDate, setDefaultStartDate] = useState('');
    const [moveLocker, setMoveLocker] = useState(true);
    const [newLockNumber, setNewLockNumber] = useState('');
    const [memo, setMemo] = useState('');
    const [recentConfirmed, setRecentConfirmed] = useState(false);
    const [failure, setFailure] = useState<FacilityTransferSubmitFailure | null>(null);
    const [changedNotice, setChangedNotice] = useState(false);
    /*
     * 확인 단계에 들어갈 때의 미리보기를 얼려 둔다. 보낸 뒤(성공·실패 모두) 목록을 새로 읽으면서 미리보기도
     * 다시 읽히는데, 결과를 모르는 채로 「다시 보내기」를 기다리는 동안 이미 줄어든 원본 기준의 새 숫자가
     * 확인 화면에 섞이면 안 된다. 다시 보내는 본문도 이 스냅숏으로 만든 lastBodyRef 그대로다.
     */
    const [confirmSnapshot, setConfirmSnapshot] = useState<FacilityTransferPreview | null>(null);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), 250);
        return () => clearTimeout(handle);
    }, [search]);
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedDaysText(partialDaysText.trim()), DAYS_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [partialDaysText]);

    const partialDays = /^\d+$/.test(debouncedDaysText) ? Number(debouncedDaysText) : null;
    const previewParams: FacilityTransferPreviewParams = useMemo(
        () => ({
            toMemberId: recipient?.id || undefined,
            days: mode === 'partial' && partialDays ? partialDays : undefined,
            startDate: startDate || undefined,
        }),
        [recipient?.id, mode, partialDays, startDate],
    );

    const previewQuery = useFacilityTransferPreview({
        centerId,
        subscriptionId: subscription._id,
        params: previewParams,
    });
    const preview = previewQuery.data?.data ?? null;
    // 미리 결제해 둔 이용권(시작 전)을 넘기면 받는 회원도 원본 시작일부터 쓰게 한다. 사용자는 바꿀 수 있다(과거만 불가).
    const baseDate = preview?.baseDate || '';
    useEffect(() => {
        if (!baseDate || defaultStartDate) return;
        setDefaultStartDate(baseDate);
        setStartDate((current) => current || baseDate);
    }, [baseDate, defaultStartDate]);
    const previewIsCurrent = isPreviewForInputs(previewQuery.data, previewParams) && !previewQuery.isFetching;

    const recentTransfers = preview?.recentTransfers ?? [];
    const recentHistory = useFacilityTransfers({
        centerId,
        subscriptionId: subscription._id,
        limit: 10,
        enabled: recentTransfers.length > 0,
    });
    const nameByTransferId = useMemo(() => {
        const map = new Map<string, string>();
        (recentHistory.data?.results || []).forEach((record) => map.set(String(record._id), record.toMemberName || ''));
        return map;
    }, [recentHistory.data]);

    const membersQuery = usePublicMemberList({
        centerId,
        search: debouncedSearch || undefined,
        status: 'active',
        limit: 30,
        enabled: !!centerId && !recipient,
    });
    const members: ICenterMember[] = useMemo(() => {
        const list: ICenterMember[] = (membersQuery.data as any)?.results ?? [];
        return list.filter((member) => resolveMemberId(member) !== String(subscription.memberId)).slice(0, 30);
    }, [membersQuery.data, subscription.memberId]);

    const transferMutation = useTransferFacilitySubscription();
    const isSubmitting = transferMutation.isLoading;

    const remainingDays = preview?.remainingDays ?? 0;
    const canPartial = remainingDays >= 2;
    const partialInvalid =
        mode === 'partial' &&
        (partialDays === null || partialDays < 1 || partialDays >= remainingDays || partialDaysText.trim() !== debouncedDaysText);
    const partialError =
        mode === 'partial' && /^\d+$/.test(partialDaysText.trim()) && remainingDays > 0 && Number(partialDaysText) >= remainingDays
            ? `남은 ${remainingDays}일을 모두 넘기려면 위에서 「전부」를 골라 주세요.`
            : mode === 'partial' && partialDaysText.trim() !== '' && (!/^\d+$/.test(partialDaysText.trim()) || Number(partialDaysText) < 1)
            ? '1일 이상 숫자로 적어 주세요.'
            : null;

    const fieldBlocker = previewIsCurrent ? describeFieldBlocker(preview?.blockedReason) : null;
    const isLockerCategory = subscription.category === 'locker';
    const lockerBasis = step === 'confirm' && confirmSnapshot ? confirmSnapshot : preview;
    const effectiveMoveLocker = isLockerCategory && !!lockerBasis?.lockerMovable && moveLocker;
    const sourceHasLocker = isLockerCategory && !!(subscription.isLockerCurrentlyAssigned ?? subscription.lockerNumber);

    const planReady =
        previewIsCurrent && !!preview && !preview.blockedReason && preview.days !== null && !!preview.targetStartDate && !partialInvalid;
    const canGoNext = planReady && !!startDate && !!recipient && (recentTransfers.length === 0 || recentConfirmed);

    const nextBlockedHint = !recipient
        ? '받는 회원을 골라 주세요.'
        : partialInvalid
        ? '넘길 일수를 확인해 주세요.'
        : recentTransfers.length > 0 && !recentConfirmed
        ? '방금 양도한 기록을 확인해 주세요.'
        : !previewIsCurrent
        ? '내용을 확인하는 중이에요…'
        : null;

    /*
     * 공통 Modal 은 ESC 리스너를 처음 렌더의 onClose 로 붙잡는다(그 시점 isSubmitting=false).
     * 그래서 Modal 에는 늘 같은 함수를 넘기고, 그 함수가 ref 로 최신 진행 상태를 본다.
     */
    const isSubmittingRef = useRef(false);
    isSubmittingRef.current = isSubmitting;
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const handleClose = useRef(() => {
        if (isSubmittingRef.current) return;
        onCloseRef.current();
    }).current;

    const buildBody = (): FacilityTransferBody | null => {
        const snapshot = confirmSnapshot;
        if (!snapshot || !recipient || snapshot.days === null || !startDate) return null;
        const lockValue = newLockNumber.trim();
        return {
            clientOperationId: operationIdRef.current,
            toMemberId: recipient.id,
            days: snapshot.days,
            startDate,
            moveLocker: effectiveMoveLocker,
            ...(effectiveMoveLocker && lockValue ? { newLockNumber: lockValue } : {}),
            ...(memo.trim() ? { memo: memo.trim() } : {}),
            // 확인 화면에 보여 준 원본 상태. 서버에서 다르면 409(그 사이 바뀜) → 새 키로 다시 접수.
            ...(snapshot.sourceEndDateBefore ? { expectedSourceEndDate: snapshot.sourceEndDateBefore } : {}),
            ...(typeof snapshot.sourceTransferCount === 'number' ? { expectedTransferCount: snapshot.sourceTransferCount } : {}),
            // 확인 화면의 전부/일부. 자정을 넘겨 기준일이 바뀌면 같은 일수가 「전부」가 될 수 있다 — 다르면 서버 409.
            ...(typeof snapshot.isFull === 'boolean' ? { expectedIsFull: snapshot.isFull } : {}),
        };
    };

    const send = async (body: FacilityTransferBody, summary: FacilityTransferPendingRecord['summary']) => {
        lastBodyRef.current = body;
        setFailure(null);
        /*
         * 보내기 직전에 이 브라우저(localStorage, 이 계정·센터)에 저장한다 — 응답 전에 창·탭을 닫거나 다시 로그인해도
         * 같은 브라우저에서 다시 열면 같은 키로 이어 보낼 수 있게. 새 요청인데 저장에 실패하면 보내지 않는다
         * (키를 잃은 채 보냈다가 응답이 끊기면 다음에 새 키로 같은 양도를 또 보낼 수 있다).
         * 결과를 모르는 요청을 다시 보낼 때는 이미 저장된 기록이 있으므로 갱신 실패를 무시한다.
         * 새 자물쇠 비밀번호는 저장하지 않는다 — 이 창 안의 재전송은 메모리의 본문(비밀번호 포함)을 그대로 쓴다.
         */
        const saved = savePendingFacilityTransfer(subscription._id, { body, summary });
        if (!saved && !unresolvedRef.current) {
            setFailure({
                kind: 'fatal',
                message: '브라우저 저장소에 기록하지 못해 양도를 보내지 않았어요. 저장 공간·사생활 보호 모드를 확인한 뒤 다시 열어 주세요.',
            });
            return;
        }
        lastSummaryRef.current = summary;
        try {
            const result = await transferMutation.mutateAsync({
                centerId,
                subscriptionId: subscription._id,
                body,
                fromMemberId: String(subscription.memberId),
            });
            if (!result || !result.transfer) {
                unresolvedRef.current = true;
                setFailure(classifyFacilityTransferFailure({ status: 0 }));
                return;
            }
            unresolvedRef.current = false;
            clearPendingFacilityTransfer(subscription._id);
            onDone(result, recipient?.name || summary.recipientName || '');
        } catch (error) {
            let classified = classifyFacilityTransferFailure(error);
            if (unresolvedRef.current && classified.kind !== 'unknown' && !isKeyTerminalResponse(error)) {
                // 같은 키 재전송의 401·403·404·400 은 앞 요청이 처리되지 않았다는 증거가 아니다 — 보관 유지.
                classified = { kind: 'unknown', message: describeResendHeld(error) };
            }
            if (classified.kind === 'unknown') {
                unresolvedRef.current = true;
            } else {
                // 첫 전송의 확정 실패, 또는 재전송의 종결 코드 — 이 키는 끝났다.
                unresolvedRef.current = false;
                clearPendingFacilityTransfer(subscription._id);
            }
            if (classified.kind === 'changed' || classified.kind === 'rejected') setRestored(null);
            if (classified.kind === 'changed') {
                // 서버가 이 키를 실패로 기록했다 — 같은 키로는 다시 접수되지 않으므로 새 키를 만든다.
                operationIdRef.current = createClientOperationId();
                lastBodyRef.current = null;
                setConfirmSnapshot(null);
                setChangedNotice(true);
                setStep('input');
                setRecentConfirmed(false);
                return;
            }
            if (classified.kind === 'rejected') {
                // 서버가 반영하지 않았다고 알려 준 거절 — 입력 단계로 돌아가 고치게 한다.
                setStep('input');
            }
            setFailure(classified);
        }
    };

    const handleSubmit = () => {
        if (isSubmitting) return;
        const body = buildBody();
        if (!body || !confirmSnapshot) return;
        send(body, {
            recipientName: recipient?.name || '',
            productName: subscription.productName,
            days: body.days,
            isFull: confirmSnapshot.isFull === true,
            targetStartDate: confirmSnapshot.targetStartDate,
            targetEndDate: confirmSnapshot.targetEndDate,
        });
    };

    const lastSummaryRef = useRef<FacilityTransferPendingRecord['summary'] | null>(restored?.summary || null);
    const handleRetrySame = () => {
        if (isSubmitting || !lastBodyRef.current) return;
        // 같은 키·처음 보낸 본문 그대로
        send(lastBodyRef.current, lastSummaryRef.current || {
            recipientName: recipient?.name || '',
            productName: subscription.productName,
            days: lastBodyRef.current.days,
            isFull: confirmSnapshot?.isFull === true,
            targetStartDate: confirmSnapshot?.targetStartDate || null,
            targetEndDate: confirmSnapshot?.targetEndDate || null,
        });
    };

    // ───────────────────────── 렌더 ─────────────────────────

    const sourceCard = (
        <Row className="items-center gap-[14px] p-[16px] rounded-[12px] bg-[#F8FAFC] border border-solid border-[#E2E8F0]">
            <div className="w-[44px] h-[44px] rounded-[12px] bg-[#EFF6FF] flex items-center justify-center text-[22px] shrink-0">
                {CATEGORY_ICON[subscription.category] || '📦'}
            </div>
            <Col className="gap-[4px] min-w-0 flex-1">
                <Row className="items-center gap-[8px] flex-wrap">
                    <span className="text-[16px] font-bold text-[#111827]">{subscription.productName}</span>
                    <span className="px-[8px] py-[2px] rounded-[6px] text-[12px] bg-[#F1F5F9] text-[#475569]">
                        {CATEGORY_LABEL[subscription.category] || subscription.category}
                    </span>
                    {sourceHasLocker && subscription.lockerNumber && (
                        <span className="px-[8px] py-[2px] rounded-[6px] text-[12px] font-bold bg-[#FEF3C7] text-[#92400E]">
                            🔐 {subscription.lockerNumber}번
                        </span>
                    )}
                </Row>
                <span className="text-[14px] text-[#64748B]">
                    {sourceMemberName}님 · {fmt(subscription.startDate)} ~{' '}
                    {/* 열린 뒤 다른 양도로 종료일이 바뀌었을 수 있어 서버 미리보기의 현재 종료일을 쓴다 */}
                    {fmt((step === 'confirm' && confirmSnapshot ? confirmSnapshot : preview)?.sourceEndDateBefore || subscription.endDate)}
                </span>
            </Col>
            {preview && !restored && !isSourceBlocker(preview.blockedReason) && (
                <Col className="items-end shrink-0">
                    <span className="text-[12px] text-[#94A3B8]">남은 기간</span>
                    <span className="text-[20px] font-bold text-[#2563EB]">
                        {(step === 'confirm' && confirmSnapshot ? confirmSnapshot : preview).remainingDays}일
                    </span>
                </Col>
            )}
        </Row>
    );

    /*
     * 탈출구: 보관 중인 미확정 요청을 버린다. 서버에서 처리되지 않은 것을 관리자가 이력에서 확인했을 때만 쓴다
     * (처리됐는데 버리면 다음 양도가 새 키로 접수돼 같은 기간이 한 번 더 넘어간다). 그래서 확인 창에 이력을 같이 보여 준다.
     */
    const discardPending = () => {
        clearPendingFacilityTransfer(subscription._id);
        unresolvedRef.current = false;
        operationIdRef.current = createClientOperationId();
        lastBodyRef.current = null;
        lastSummaryRef.current = null;
        setConfirmSnapshot(null);
        setRestored(null);
        setFailure(null);
        setStep('input');
        setRecentConfirmed(false);
        setShowDiscardConfirm(false);
    };
    const discardLink = (
        <button
            type="button"
            disabled={isSubmitting}
            onClick={() => setShowDiscardConfirm(true)}
            className="self-start mt-[2px] p-0 border-0 bg-transparent text-[12px] text-[#94A3B8] underline cursor-pointer"
        >
            이 요청 버리기
        </button>
    );

    const renderRestored = () => {
        if (!restored) return null;
        const sum = restored.summary;
        return (
            <Col className="gap-[12px]">
                <Note tone="warn">
                    <b className="text-[14px]">결과를 확인하지 못한 양도가 있어요</b>
                    <span className="block mt-[4px]">
                        {sum.recipientName ? `${sum.recipientName}님께 ` : ''}
                        {sum.days}일{sum.isFull ? '(전부)' : ''} · 받는 기간 {fmt(sum.targetStartDate)} ~ {fmt(sum.targetEndDate)}
                    </span>
                    <span className="block text-[12px] text-[#B45309]">{dayjs(restored.savedAt).format('M월 D일 HH:mm')}에 보냄</span>
                    <span className="block mt-[6px]">
                        이미 처리됐으면 결과만 보여 주고, 아직 안 됐으면 지금 처리돼요. 두 번 양도되지 않아요.
                        <br />
                        이 요청을 마치기 전에는 새로 양도할 수 없어요.
                    </span>
                </Note>
                {restored.body.moveLocker && (
                    // 새 자물쇠 비밀번호는 보관하지 않는다. 첫 요청이 서버에 닿지 않았다면 이번 요청이 새 양도가 되어
                    // 칸이 새 비밀번호 없이 넘어간다.
                    <Note tone="info">새 자물쇠 비밀번호는 보관하지 않아 이번에는 빠진 채로 보내요. 양도 뒤 「락커 관리」에서 비밀번호를 확인해 주세요.</Note>
                )}
                {failure && failure.kind !== 'changed' && failure.kind !== 'rejected' && (
                    <Note tone={failure.kind === 'unknown' ? 'warn' : 'danger'}>{failure.message}</Note>
                )}
                {(!failure || failure.kind === 'unknown') && discardLink}
            </Col>
        );
    };

    const renderBody = () => {
        if (pendingReadFailed) {
            return (
                <Col className="gap-[10px]">
                    <Note tone="warn">
                        <b>저장된 양도 요청을 확인하지 못했어요.</b>
                        <br />
                        결과를 모르는 요청이 남아 있을 수 있어, 확인하기 전에는 새로 양도할 수 없어요.
                    </Note>
                    <button
                        type="button"
                        onClick={() => {
                            const again = readPendingFacilityTransfer(subscription._id);
                            if (!again.ok) return;
                            setPendingReadFailed(false);
                            if (again.record) {
                                operationIdRef.current = again.record.body.clientOperationId;
                                lastBodyRef.current = again.record.body;
                                lastSummaryRef.current = again.record.summary;
                                unresolvedRef.current = true;
                                setRestored(again.record);
                            }
                        }}
                        className="self-start h-[34px] px-[14px] rounded-[8px] border border-solid border-[#D1D5DB] bg-white text-[13px] font-semibold text-[#374151] cursor-pointer"
                    >
                        다시 읽기
                    </button>
                </Col>
            );
        }
        if (restored) return renderRestored();
        if (previewQuery.isLoading && !previewQuery.data) {
            return (
                <Col className="gap-[12px]">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-[72px] rounded-[12px] bg-[#F1F5F9] animate-pulse" />
                    ))}
                </Col>
            );
        }
        if (previewQuery.isError && !previewQuery.data) {
            if (isFacilityTransferUnsupported(previewQuery.error)) {
                return (
                    <Note tone="muted">
                        <b>이 서버에서는 아직 이용권 양도를 쓸 수 없어요.</b>
                        <br />
                        서버 업데이트가 끝나면 이 버튼으로 바로 양도할 수 있어요.
                    </Note>
                );
            }
            const err: any = previewQuery.error;
            return (
                <Col className="gap-[10px]">
                    <Note tone="danger">{err?.serverMessage || err?.message || '양도 정보를 불러오지 못했어요.'}</Note>
                    <button
                        type="button"
                        onClick={() => previewQuery.refetch()}
                        className="self-start h-[34px] px-[14px] rounded-[8px] border border-solid border-[#D1D5DB] bg-white text-[13px] font-semibold text-[#374151] cursor-pointer"
                    >
                        다시 불러오기
                    </button>
                </Col>
            );
        }
        if (preview && isSourceBlocker(preview.blockedReason) && step === 'input') {
            const blocker = describeSourceBlocker(preview.blockedReason);
            return (
                <Note tone="warn">
                    <b className="text-[14px]">{blocker?.title}</b>
                    <br />
                    <span style={{ whiteSpace: 'pre-line' }}>{blocker?.message}</span>
                </Note>
            );
        }
        if (!preview) return null;
        return step === 'input' ? renderInput() : renderConfirm();
    };

    const renderRecent = () => {
        if (recentTransfers.length === 0) return null;
        return (
            <Note tone="warn">
                <b>방금 이 이용권을 양도했어요.</b>
                <Col className="gap-[2px] mt-[4px]">
                    {recentTransfers.map((item) => {
                        const name = nameByTransferId.get(String(item.transferId));
                        return (
                            <span key={item.transferId}>
                                · {dayjs(item.at).format('HH:mm')} {name ? `${name}님께` : '다른 회원에게'} {item.days}일
                                {item.isFull ? ' (전부)' : ''}
                            </span>
                        );
                    })}
                </Col>
                <label className="flex items-center gap-[8px] mt-[8px] cursor-pointer font-semibold">
                    <input
                        type="checkbox"
                        checked={recentConfirmed}
                        onChange={(e) => setRecentConfirmed(e.target.checked)}
                        style={{ width: 16, height: 16 }}
                    />
                    알고 있어요. 한 번 더 양도할게요.
                </label>
            </Note>
        );
    };

    const renderRecipient = () => (
        <Col>
            <SectionTitle step={1} title="받는 회원" hint="같은 센터 회원만" />
            {recipient ? (
                <Row className="items-center justify-between gap-[12px] p-[12px] rounded-[10px] border border-solid border-[#93C5FD] bg-[#EFF6FF]">
                    <Col className="gap-[2px] min-w-0">
                        <Row className="items-center gap-[6px] flex-wrap">
                            <span className="text-[16px] font-bold text-[#0F172A]">{recipient.name}</span>
                            <AppAccountStatusBadge member={recipient.member} />
                        </Row>
                        <span className="text-[13px] text-[#475569]">{formatKoreanPhoneNumber(recipient.phoneNumber, '-')}</span>
                    </Col>
                    <button
                        type="button"
                        onClick={() => setRecipient(null)}
                        className="h-[32px] px-[12px] rounded-[8px] border border-solid border-[#BFDBFE] bg-white text-[13px] font-bold text-[#2563EB] cursor-pointer shrink-0"
                    >
                        변경
                    </button>
                </Row>
            ) : (
                <Col className="gap-[8px]">
                    <input
                        autoFocus
                        className="h-[42px] px-[12px] rounded-[10px] border border-solid border-[#E1E4E6] text-[14px] outline-none focus:border-[#60A5FA]"
                        placeholder="이름 또는 휴대폰 번호로 찾기"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Col className="max-h-[176px] overflow-y-auto rounded-[10px] border border-solid border-[#E5E7EB]">
                        {membersQuery.isLoading ? (
                            <span className="p-[14px] text-[13px] text-[#9CA3AF]">회원을 찾는 중이에요…</span>
                        ) : membersQuery.isError ? (
                            <span className="p-[14px] text-[13px] text-[#DC2626]">회원 목록을 불러오지 못했어요.</span>
                        ) : members.length === 0 ? (
                            <span className="p-[14px] text-[13px] text-[#9CA3AF]">
                                {debouncedSearch ? '찾는 회원이 없어요.' : '회원이 없어요.'}
                            </span>
                        ) : (
                            members.map((member) => {
                                const id = resolveMemberId(member);
                                // 앱 탈퇴 회원은 서버가 받는 회원으로 거절한다 — 고르기 전에 막는다.
                                const withdrawn = getMemberAppAccountStatus(member) === 'withdrawn';
                                return (
                                    <button
                                        key={id}
                                        type="button"
                                        disabled={withdrawn}
                                        title={withdrawn ? '앱을 탈퇴한 회원에게는 넘길 수 없어요' : undefined}
                                        onClick={() => {
                                            if (withdrawn) return;
                                            setRecipient({ id, name: member.name, phoneNumber: member.phoneNumber, member });
                                            setSearch('');
                                        }}
                                        className={`flex items-center justify-between gap-[10px] px-[12px] py-[9px] bg-white text-left border-0 border-b border-solid border-[#F3F4F6] ${
                                            withdrawn ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-[#F8FAFC]'
                                        }`}
                                    >
                                        <Row className="items-center gap-[6px] min-w-0">
                                            <span className="text-[14px] font-semibold text-[#111827]">{member.name}</span>
                                            <AppAccountStatusBadge member={member} />
                                        </Row>
                                        <span className="text-[13px] text-[#6B7280] shrink-0">
                                            {formatKoreanPhoneNumber(member.phoneNumber, '-')}
                                        </span>
                                    </button>
                                );
                            })
                        )}
                    </Col>
                </Col>
            )}
            <FieldError message={fieldBlocker?.field === 'recipient' ? fieldBlocker.message : null} />
            {recipient && previewIsCurrent && (preview?.overlaps?.length ?? 0) > 0 && (
                <div className="mt-[8px]">
                    <Note tone="warn">
                        {recipient.name}님은 이미 같은 종류 이용권을 쓰고 있어요. 양도는 할 수 있어요.
                        {preview!.overlaps.map((o) => (
                            <span key={o.subscriptionId} className="block">
                                · {o.productName} ({fmt(o.startDate)} ~ {fmt(o.endDate)})
                            </span>
                        ))}
                    </Note>
                </div>
            )}
        </Col>
    );

    const renderDays = () => {
        const chip = (active: boolean, disabled = false) =>
            `flex-1 h-[52px] rounded-[10px] border border-solid text-left px-[14px] ${
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
            } ${active ? 'border-[#3B82F6] bg-[#EFF6FF]' : 'border-[#E1E4E6] bg-white'}`;
        return (
            <Col>
                <SectionTitle step={2} title="넘길 기간" />
                <Row className="gap-[8px]">
                    <button type="button" className={chip(mode === 'full')} onClick={() => setMode('full')}>
                        <span className={`block text-[14px] font-bold ${mode === 'full' ? 'text-[#1D4ED8]' : 'text-[#111827]'}`}>전부</span>
                        <span className="block text-[12px] text-[#64748B]">남은 {remainingDays}일 모두</span>
                    </button>
                    <button
                        type="button"
                        disabled={!canPartial}
                        className={chip(mode === 'partial', !canPartial)}
                        onClick={() => canPartial && setMode('partial')}
                    >
                        <span className={`block text-[14px] font-bold ${mode === 'partial' ? 'text-[#1D4ED8]' : 'text-[#111827]'}`}>일부만</span>
                        <span className="block text-[12px] text-[#64748B]">{canPartial ? '며칠만 넘기기' : '남은 기간이 하루라 전부만 돼요'}</span>
                    </button>
                </Row>
                {mode === 'partial' && (
                    <Row className="items-center gap-[8px] mt-[10px]">
                        <input
                            autoFocus
                            inputMode="numeric"
                            className="w-[96px] h-[42px] px-[12px] rounded-[10px] border border-solid border-[#E1E4E6] text-[16px] font-semibold text-right outline-none focus:border-[#60A5FA]"
                            placeholder="0"
                            value={partialDaysText}
                            onChange={(e) => setPartialDaysText(e.target.value.replace(/[^\d]/g, '').slice(0, 4))}
                        />
                        <span className="text-[14px] text-[#374151]">일</span>
                        <span className="text-[13px] text-[#9CA3AF]">(1 ~ {Math.max(1, remainingDays - 1)}일)</span>
                    </Row>
                )}
                <FieldError message={partialError || (fieldBlocker?.field === 'days' ? fieldBlocker.message : null)} />
                {planReady && preview && !preview.isFull && (
                    <span className="text-[13px] text-[#475569] mt-[8px]">
                        {sourceMemberName}님은 <b>{fmtShort(preview.sourceEndDateAfter)}</b>까지 쓰고, 뒤쪽 {preview.days}일을{' '}
                        {recipient ? `${recipient.name}님께` : ''} 넘겨요.
                    </span>
                )}
            </Col>
        );
    };

    const renderStartDate = () => (
        <Col>
            <SectionTitle step={3} title="받는 회원 시작일" />
            <Row className="items-center gap-[8px] flex-wrap">
                <input
                    type="date"
                    min={todayKey()}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="h-[42px] px-[12px] rounded-[10px] border border-solid border-[#E1E4E6] text-[14px] outline-none focus:border-[#60A5FA]"
                />
                {defaultStartDate && startDate !== defaultStartDate && (
                    <button
                        type="button"
                        onClick={() => setStartDate(defaultStartDate)}
                        className="h-[32px] px-[10px] rounded-[8px] border border-solid border-[#E5E7EB] bg-[#F9FAFB] text-[12px] text-[#374151] cursor-pointer"
                    >
                        {defaultStartDate === todayKey() ? '오늘로' : `${fmtShort(defaultStartDate)}로`}
                    </button>
                )}
                {planReady && preview && (
                    <span className="text-[13px] text-[#475569]">
                        → <b>{fmt(preview.targetStartDate)} ~ {fmt(preview.targetEndDate)}</b> ({preview.days}일)
                    </span>
                )}
            </Row>
            <FieldError message={fieldBlocker?.field === 'startDate' ? fieldBlocker.message : null} />
            {planReady && preview && preview.targetStartDate && dayjs(preview.targetStartDate).format('YYYY-MM-DD') > todayKey() && (
                <span className="text-[12px] text-[#94A3B8] mt-[6px]">시작일 전에는 받는 회원이 입장할 수 없어요.</span>
            )}
        </Col>
    );

    const renderLocker = () => {
        if (!isLockerCategory || !preview) return null;
        const blocked = describeLockerBlocked(preview.lockerBlockedReason);
        const movable = !!preview.lockerMovable;
        return (
            <Col>
                <SectionTitle step={4} title="락커 칸" />
                <Col className="gap-[10px] p-[14px] rounded-[10px] border border-solid border-[#E5E7EB] bg-white">
                    <label className={`flex items-center gap-[10px] ${movable ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                        <input
                            type="checkbox"
                            disabled={!movable}
                            checked={movable && moveLocker}
                            onChange={(e) => setMoveLocker(e.target.checked)}
                            style={{ width: 18, height: 18 }}
                        />
                        <span className={`text-[14px] font-semibold ${movable ? 'text-[#111827]' : 'text-[#9CA3AF]'}`}>
                            {subscription.lockerNumber ? `${subscription.lockerNumber}번 칸도 함께 넘기기` : '칸도 함께 넘기기'}
                        </span>
                    </label>
                    {!movable && blocked && <span className="text-[13px] text-[#64748B] leading-[1.5]">{blocked}</span>}
                    {movable && !moveLocker && (
                        <span className="text-[13px] text-[#64748B]">칸은 비워지고, 받는 회원에겐 칸 없이 넘어가요.</span>
                    )}
                    {movable && moveLocker && (
                        <Col className="gap-[6px]">
                            {preview.lockerHasFixedLockNumber ? (
                                <Note tone="warn">이 칸은 고정 비밀번호를 써서 원래 회원도 비밀번호를 알아요. 새 비밀번호로 바꿔 주세요.</Note>
                            ) : (
                                <span className="text-[13px] text-[#64748B]">원래 회원의 비밀번호는 넘어가지 않아요.</span>
                            )}
                            <input
                                className="h-[40px] px-[12px] rounded-[10px] border border-solid border-[#E1E4E6] text-[14px] outline-none focus:border-[#60A5FA]"
                                placeholder="새 비밀번호 (선택)"
                                maxLength={50}
                                value={newLockNumber}
                                onChange={(e) => setNewLockNumber(e.target.value)}
                            />
                        </Col>
                    )}
                </Col>
            </Col>
        );
    };

    const renderInput = () => (
        <Col className="gap-[22px]">
            {changedNotice && (
                <Note tone="info">앞서 보낸 양도는 반영되지 않았어요. 최신 내용으로 다시 보여 드려요. 한 번 더 확인해 주세요.</Note>
            )}
            {failure && failure.kind === 'rejected' && <Note tone="danger">{failure.message}</Note>}
            {renderRecent()}
            {renderRecipient()}
            {renderDays()}
            {renderStartDate()}
            {renderLocker()}
            <Col>
                <span className="text-[14px] font-semibold text-[#374151] mb-[8px]">메모 (선택)</span>
                <input
                    className="h-[40px] px-[12px] rounded-[10px] border border-solid border-[#E1E4E6] text-[14px] outline-none focus:border-[#60A5FA]"
                    placeholder="예: 회원 요청으로 가족에게 양도"
                    maxLength={500}
                    value={memo}
                    onChange={(e) => setMemo(e.target.value)}
                />
            </Col>
        </Col>
    );

    const ConfirmRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
        <Row className="justify-between items-start gap-[10px] py-[8px] border-0 border-t border-solid border-[#F1F5F9] first:border-t-0">
            <span className="text-[13px] text-[#64748B] shrink-0">{label}</span>
            <span className="text-[14px] text-[#111827] text-right">{children}</span>
        </Row>
    );

    const renderConfirm = () => {
        const preview = confirmSnapshot;
        if (!preview || !recipient) return null;
        const isFull = preview.isFull === true;
        const sourceLocker = !sourceHasLocker
            ? null
            : effectiveMoveLocker
            ? `${subscription.lockerNumber}번 → ${recipient.name}님`
            : isFull
            ? '칸 비움(회수)'
            : `${subscription.lockerNumber}번 계속 사용`;
        const targetLocker = !isLockerCategory
            ? null
            : effectiveMoveLocker
            ? `${subscription.lockerNumber}번${newLockNumber.trim() ? ' · 새 비밀번호' : ''}`
            : '칸 없음 (양도 후 따로 배정)';
        return (
            <Col className="gap-[16px]">
                <span className="text-[18px] font-bold text-[#111827]">이대로 양도할까요?</span>
                <Row className="gap-[12px] items-stretch">
                    <Col className="flex-1 p-[16px] rounded-[12px] border border-solid border-[#E2E8F0] bg-[#F8FAFC]">
                        <span className="text-[12px] font-semibold text-[#64748B]">넘겨 주는 회원</span>
                        <span className="text-[17px] font-bold text-[#111827] mb-[8px]">{sourceMemberName}</span>
                        <ConfirmRow label="이용 기간">
                            {isFull ? (
                                <span className="font-semibold text-[#B91C1C]">전부 넘김 · 이용 끝</span>
                            ) : (
                                <>
                                    <span className="line-through text-[#94A3B8] mr-[6px]">~{fmt(preview.lastUsableDate)}</span>
                                    <b>~{fmt(preview.sourceEndDateAfter)}</b>
                                </>
                            )}
                        </ConfirmRow>
                        <ConfirmRow label="결제">그대로 (매출 유지)</ConfirmRow>
                        {sourceLocker && <ConfirmRow label="락커">{sourceLocker}</ConfirmRow>}
                    </Col>
                    <div className="flex items-center text-[22px] text-[#93C5FD]">→</div>
                    <Col className="flex-1 p-[16px] rounded-[12px] border border-solid border-[#93C5FD] bg-[#EFF6FF]">
                        <span className="text-[12px] font-semibold text-[#2563EB]">받는 회원</span>
                        <span className="text-[17px] font-bold text-[#111827] mb-[8px]">{recipient.name}</span>
                        <ConfirmRow label="이용 기간">
                            <b>
                                {fmt(preview.targetStartDate)} ~ {fmt(preview.targetEndDate)}
                            </b>
                            <span className="block text-[12px] text-[#64748B]">{preview.days}일</span>
                        </ConfirmRow>
                        <ConfirmRow label="금액">
                            <b>0원</b>
                        </ConfirmRow>
                        {targetLocker && <ConfirmRow label="락커">{targetLocker}</ConfirmRow>}
                    </Col>
                </Row>
                {(preview.overlaps?.length ?? 0) > 0 && (
                    <Note tone="warn">{recipient.name}님은 이미 같은 종류 이용권을 쓰고 있어요.</Note>
                )}
                {effectiveMoveLocker && preview.lockerHasFixedLockNumber && !newLockNumber.trim() && (
                    <Note tone="warn">새 비밀번호 없이 넘기면 원래 회원도 칸을 열 수 있어요.</Note>
                )}
                <Note tone="muted">
                    매출은 {sourceMemberName}님의 결제에 그대로 남고, {recipient.name}님 이용권은 0원으로 만들어져요.
                    <br />
                    양도한 뒤에는 화면에서 되돌릴 수 없어요.
                </Note>
                {failure && failure.kind !== 'rejected' && (
                    <Note tone={failure.kind === 'unknown' ? 'warn' : 'danger'}>
                        {failure.message}
                        {failure.kind === 'unknown' && (
                            <button
                                type="button"
                                onClick={handleRetrySame}
                                disabled={isSubmitting}
                                className="block mt-[8px] h-[32px] px-[14px] rounded-[8px] border-0 bg-[#D97706] text-white text-[13px] font-bold cursor-pointer"
                            >
                                {isSubmitting ? '보내는 중…' : '다시 보내기'}
                            </button>
                        )}
                    </Note>
                )}
                {failure?.kind === 'unknown' && discardLink}
            </Col>
        );
    };

    // 하단 버튼
    const isBlockedView =
        pendingReadFailed ||
        (previewQuery.isError && !previewQuery.data) || (!!preview && isSourceBlocker(preview.blockedReason) && step === 'input');
    const fatal = failure?.kind === 'fatal' || failure?.kind === 'unsupported';
    const awaitingRetry = failure?.kind === 'unknown';

    const btn = 'h-[44px] px-[22px] rounded-[10px] text-[15px] font-bold cursor-pointer border border-solid';
    const footer = (() => {
        if (restored) {
            const canCheck = failure?.kind !== 'fatal' && failure?.kind !== 'unsupported';
            return (
                <Row className="justify-end gap-[8px]">
                    <button type="button" disabled={isSubmitting} onClick={handleClose} className={`${btn} border-[#D1D5DB] bg-white text-[#374151]`}>
                        닫기
                    </button>
                    {canCheck && (
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={handleRetrySame}
                            className={`${btn} border-transparent bg-[#D97706] text-white`}
                            style={{ opacity: isSubmitting ? 0.6 : 1 }}
                        >
                            {isSubmitting ? '처리하는 중…' : '이어서 양도하기'}
                        </button>
                    )}
                </Row>
            );
        }
        if (isBlockedView || fatal) {
            return (
                <Row className="justify-end">
                    <button type="button" onClick={handleClose} className={`${btn} border-[#D1D5DB] bg-white text-[#374151]`}>
                        닫기
                    </button>
                </Row>
            );
        }
        if (step === 'input') {
            return (
                <Row className="justify-between items-center gap-[12px]">
                    <span className="text-[13px] text-[#94A3B8]">{nextBlockedHint || ''}</span>
                    <Row className="gap-[8px]">
                        <button type="button" onClick={handleClose} className={`${btn} border-[#D1D5DB] bg-white text-[#374151]`}>
                            취소
                        </button>
                        <button
                            type="button"
                            disabled={!canGoNext}
                            onClick={() => {
                                setFailure(null);
                                setChangedNotice(false);
                                setConfirmSnapshot(preview);
                                setStep('confirm');
                            }}
                            className={`${btn} border-transparent text-white ${canGoNext ? 'bg-[#2563EB]' : 'bg-[#BFDBFE] cursor-not-allowed'}`}
                        >
                            다음
                        </button>
                    </Row>
                </Row>
            );
        }
        return (
            <Row className="justify-between items-center">
                {/* 결과를 모르는 동안에는 입력으로 돌아가지 못한다 — 같은 키·같은 본문의 「다시 보내기」나 닫기만 */}
                {awaitingRetry ? (
                    <button type="button" disabled={isSubmitting} onClick={handleClose} className={`${btn} border-[#D1D5DB] bg-white text-[#374151]`}>
                        닫기
                    </button>
                ) : (
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => {
                            setFailure(null);
                            setStep('input');
                        }}
                        className={`${btn} border-[#D1D5DB] bg-white text-[#374151]`}
                    >
                        이전
                    </button>
                )}
                {!awaitingRetry && (
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                        className={`${btn} border-transparent bg-[#2563EB] text-white`}
                        style={{ opacity: isSubmitting ? 0.6 : 1 }}
                    >
                        {isSubmitting ? '양도하는 중…' : '양도하기'}
                    </button>
                )}
            </Row>
        );
    })();

    return (
        <Modal whiteViewWidth={640} isCloseOutside={false} isWhiteOverFlowScroll={false} onClose={handleClose} isCloseButton modalAlign="start">
            <Col className="px-[32px] pt-[28px] pb-[24px] gap-[20px]">
                <Col className="gap-[4px] pr-[40px]">
                    <span className="text-[22px] font-bold text-[#111827]">이용권 양도</span>
                    <span className="text-[14px] text-[#64748B]">남은 기간을 같은 센터의 다른 회원에게 넘겨요.</span>
                </Col>
                {sourceCard}
                {/* 본문만 스크롤하고 머리(이용권 요약)·발(버튼)은 늘 보이게 한다. 스크롤 상자는 flex 가 아닌 블록으로 둔다. */}
                <div className="overflow-y-auto pr-[2px]" style={{ maxHeight: 'calc(100vh - 460px)' }}>
                    {renderBody()}
                </div>
                {failure?.kind === 'unsupported' && <Note tone="muted">{failure.message}</Note>}
                {footer}
            </Col>
            {showDiscardConfirm && (
                <div
                    className="fixed inset-0 flex items-center justify-center"
                    style={{ zIndex: 2100, backgroundColor: 'rgba(0,0,0,0.35)' }}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <Col className="w-[520px] bg-white rounded-[14px] p-[24px] gap-[14px]" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                        <span className="text-[18px] font-bold text-[#111827]">이 요청을 버릴까요?</span>
                        <span className="text-[14px] leading-[1.6] text-[#374151]">
                            양도 이력에서 이 양도가 처리되지 않은 것을 확인했나요?
                            <br />
                            <b className="text-[#B91C1C]">처리됐는데 버리면 같은 양도가 한 번 더 될 수 있어요.</b>
                        </span>
                        <Col className="gap-[6px]">
                            <span className="text-[13px] font-semibold text-[#64748B]">이 이용권의 양도 이력</span>
                            <div className="max-h-[240px] overflow-y-auto">
                                <SubscriptionTransferHistoryList centerId={centerId} subscriptionId={subscription._id} limit={20} />
                            </div>
                        </Col>
                        <Row className="justify-end gap-[8px] mt-[4px]">
                            <button
                                type="button"
                                onClick={() => setShowDiscardConfirm(false)}
                                className="h-[40px] px-[18px] rounded-[10px] border border-solid border-[#D1D5DB] bg-white text-[14px] font-bold text-[#374151] cursor-pointer"
                            >
                                취소
                            </button>
                            <button
                                type="button"
                                onClick={discardPending}
                                className="h-[40px] px-[18px] rounded-[10px] border-0 bg-[#DC2626] text-[14px] font-bold text-white cursor-pointer"
                            >
                                버리기
                            </button>
                        </Row>
                    </Col>
                </div>
            )}
        </Modal>
    );
};

export default SubscriptionTransferModal;
